import {
  ApolloClient,
  ApolloLink,
  DocumentNode,
  FetchResult,
  InMemoryCache,
  MutationHookOptions,
  MutationTuple,
  NoInfer,
  Observable,
  Operation,
  OperationVariables,
  TypedDocumentNode,
  createHttpLink,
  useMutation,
  useQuery,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { getMainDefinition } from '@apollo/client/utilities';
import { print } from 'graphql';
import { createClient, ClientOptions, Client } from 'graphql-sse';

import { getEnv } from 'libs/contexts/env';

export enum GQLContexts {
  CLIENT_API = 'CLIENT_API',
}

declare module '@apollo/client' {
  interface DefaultContext {
    clientName: GQLContexts;
  }

  function useMutation<TData = any, TVariables = OperationVariables>(
    mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
    options?: MutationHookOptions<NoInfer<TData>, NoInfer<TVariables>>,
  ): MutationTuple<TData, TVariables>;
}

// This `SSELink` class originates from a public `graphql-sse` recipe/example.
// See: https://the-guild.dev/graphql/sse/recipes#with-apollo
class SSELink extends ApolloLink {
  private client: Client;

  constructor(options: ClientOptions) {
    super();
    this.client = createClient(options);
  }

  public request(operation: Operation): Observable<FetchResult> {
    return new Observable(sink => {
      return this.client.subscribe<FetchResult>(
        { ...operation, query: print(operation.query) },
        {
          next: sink.next.bind(sink),
          complete: sink.complete.bind(sink),
          error: sink.error.bind(sink),
        },
      );
    });
  }
}

const createSSELink = (url: string, authToken?: string) => {
  return new SSELink({
    url,
    headers: () => ({ authorization: `Bearer ${authToken}` }),
  });
};

const createApolloClient = (authToken?: string) => {
  const clientApiUri = getEnv('CLIENT_API_ENDPOINT');
  const sseClientApiUri = getEnv('SSE_CLIENT_API_ENDPOINT');
  const authLink = setContext(async (_, previousContext) => {
    if (authToken) {
      return {
        ...previousContext,
        headers: {
          ...previousContext.headers,
          authorization: `Bearer ${authToken}`,
        },
      };
    }

    return previousContext;
  });

  const clientAPIHttpLink = createHttpLink({ uri: clientApiUri });
  const sseLink = createSSELink(sseClientApiUri, authToken);

  const link = ApolloLink.from([authLink]).split(
    op => {
      const definition = getMainDefinition(op.query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    sseLink,
    clientAPIHttpLink,
  );

  return new ApolloClient({
    link,
    clientAwareness: { name: 'discovery' },
    cache: new InMemoryCache(),
  });
};

const useClientQuery: typeof useQuery = (...params) => {
  return useQuery(params[0], {
    ...params[1],
    context: { clientName: GQLContexts.CLIENT_API },
  });
};

const useClientMutation: typeof useMutation = (...params) => {
  return useMutation(params[0], {
    ...params[1],
    context: { clientName: '' },
  });
};

export { createApolloClient, useClientQuery, useClientMutation };
